/** @module @liquidefy/code-editor/diff/diff-render @version 1.0.0 @license LQD Research LLC */

// ═══ §1 IMPORTS ═══

import type {
  DiffResult,
  DiffHunk,
  DiffLine,
  DiffStats,
  DiffRenderMode,
  DiffId,
  HunkId,
  CharRange,
  DecorationData,
  DecorationId,
  Range,
  EditorResult,
} from '../code-editor-types.js';

import {
  ok,
  err,
  generateId,
  asDiffId,
  asHunkId,
  asDecorationId,
  DEFAULT_DIFF_CONFIG,
} from '../code-editor.js';

// ═══ §2 UNIFIED FORMAT — formatUnifiedDiff, formatPatchHeader, formatHunkHeader, formatDiffLine, parseUnifiedDiff ═══

export function formatUnifiedDiff(result: DiffResult, contextLines?: number): string {
  const lines: string[] = [];
  lines.push(formatPatchHeader(result.id));
  for (const hunk of result.hunks) {
    lines.push(formatHunkHeader(hunk));
    for (const line of hunk.lines) {
      lines.push(formatDiffLine(line));
    }
  }
  return lines.join('\n');
}

export function formatPatchHeader(filePath: string, original?: string, modified?: string): string {
  const origLabel = original ?? `a/${filePath}`;
  const modLabel = modified ?? `b/${filePath}`;
  return `--- ${origLabel}\n+++ ${modLabel}`;
}

export function formatHunkHeader(hunk: DiffHunk): string {
  return `@@ -${hunk.oldStart},${hunk.oldCount} +${hunk.newStart},${hunk.newCount} @@`;
}

export function formatDiffLine(line: DiffLine): string {
  switch (line.type) {
    case 'add': return `+${line.content}`;
    case 'delete': return `-${line.content}`;
    case 'equal':
    case 'context':
    default: return ` ${line.content}`;
  }
}

export function parseUnifiedDiff(text: string): EditorResult<DiffResult> {
  const lines = text.split('\n');
  const hunks: DiffHunk[] = [];
  let currentHunkLines: DiffLine[] = [];
  let hunkOldStart = 0;
  let hunkOldCount = 0;
  let hunkNewStart = 0;
  let hunkNewCount = 0;
  let oldLine = 0;
  let newLine = 0;
  let inHunk = false;

  for (const rawLine of lines) {
    if (rawLine.startsWith('@@')) {
      if (inHunk && currentHunkLines.length > 0) {
        hunks.push({
          id: asHunkId(generateId('hunk-')),
          oldStart: hunkOldStart,
          oldCount: hunkOldCount,
          newStart: hunkNewStart,
          newCount: hunkNewCount,
          lines: currentHunkLines,
          isConflict: false,
        });
        currentHunkLines = [];
      }
      const match = rawLine.match(/@@ -(\d+),?(\d*) \+(\d+),?(\d*) @@/);
      if (match) {
        hunkOldStart = parseInt(match[1]!, 10);
        hunkOldCount = match[2] ? parseInt(match[2], 10) : 1;
        hunkNewStart = parseInt(match[3]!, 10);
        hunkNewCount = match[4] ? parseInt(match[4], 10) : 1;
        oldLine = hunkOldStart;
        newLine = hunkNewStart;
        inHunk = true;
      }
    } else if (inHunk) {
      if (rawLine.startsWith('+')) {
        currentHunkLines.push({
          type: 'add',
          content: rawLine.substring(1),
          newLineNumber: newLine,
        });
        newLine++;
      } else if (rawLine.startsWith('-')) {
        currentHunkLines.push({
          type: 'delete',
          content: rawLine.substring(1),
          oldLineNumber: oldLine,
        });
        oldLine++;
      } else if (rawLine.startsWith(' ') || rawLine === '') {
        currentHunkLines.push({
          type: 'equal',
          content: rawLine.length > 0 ? rawLine.substring(1) : '',
          oldLineNumber: oldLine,
          newLineNumber: newLine,
        });
        oldLine++;
        newLine++;
      }
    }
  }

  if (inHunk && currentHunkLines.length > 0) {
    hunks.push({
      id: asHunkId(generateId('hunk-')),
      oldStart: hunkOldStart,
      oldCount: hunkOldCount,
      newStart: hunkNewStart,
      newCount: hunkNewCount,
      lines: currentHunkLines,
      isConflict: false,
    });
  }

  let additions = 0;
  let deletions = 0;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type === 'add') additions++;
      else if (line.type === 'delete') deletions++;
    }
  }

  const result: DiffResult = {
    id: asDiffId(generateId('diff-')),
    algorithm: 'myers',
    original: '',
    modified: '',
    hunks,
    stats: { additions, deletions, changes: additions + deletions },
  };
  return ok(result, 'DIFF_PARSED', `Parsed ${hunks.length} hunks from unified diff`);
}

// ═══ §3 SIDE-BY-SIDE — prepareSideBySide, alignLines, insertGapLines, sideBySideWidth ═══

export interface SideBySideLine {
  readonly lineNo: number | undefined;
  readonly content: string;
  readonly type: 'equal' | 'add' | 'delete' | 'gap';
  readonly highlight?: CharRange[];
}

export function prepareSideBySide(
  hunks: DiffHunk[]
): { left: SideBySideLine[]; right: SideBySideLine[] } {
  const left: SideBySideLine[] = [];
  const right: SideBySideLine[] = [];

  for (const hunk of hunks) {
    const aligned = alignLines(hunk);
    left.push(...aligned.left);
    right.push(...aligned.right);
  }
  return { left, right };
}

export function alignLines(hunk: DiffHunk): { left: SideBySideLine[]; right: SideBySideLine[] } {
  const left: SideBySideLine[] = [];
  const right: SideBySideLine[] = [];

  let i = 0;
  while (i < hunk.lines.length) {
    const line = hunk.lines[i];
    if (line!.type === 'equal') {
      left.push({
        lineNo: line!.oldLineNumber,
        content: line!.content,
        type: 'equal',
      });
      right.push({
        lineNo: line!.newLineNumber,
        content: line!.content,
        type: 'equal',
      });
      i++;
    } else if (line!.type === 'delete') {
      const deleteBlock: DiffLine[] = [];
      while (i < hunk.lines.length && hunk.lines[i]!.type === 'delete') {
        deleteBlock.push(hunk.lines[i]!);
        i++;
      }
      const addBlock: DiffLine[] = [];
      while (i < hunk.lines.length && hunk.lines[i]!.type === 'add') {
        addBlock.push(hunk.lines[i]!);
        i++;
      }
      const maxLen = Math.max(deleteBlock.length, addBlock.length);
      for (let j = 0; j < maxLen; j++) {
        if (j < deleteBlock.length) {
          left.push({
            lineNo: deleteBlock[j]!.oldLineNumber,
            content: deleteBlock[j]!.content,
            type: 'delete',
            highlight: deleteBlock[j]!.charChanges,
          });
        } else {
          left.push({ lineNo: undefined, content: '', type: 'gap' });
        }
        if (j < addBlock.length) {
          right.push({
            lineNo: addBlock[j]!.newLineNumber,
            content: addBlock[j]!.content,
            type: 'add',
            highlight: addBlock[j]!.charChanges,
          });
        } else {
          right.push({ lineNo: undefined, content: '', type: 'gap' });
        }
      }
    } else if (line!.type === 'add') {
      left.push({ lineNo: undefined, content: '', type: 'gap' });
      right.push({
        lineNo: line!.newLineNumber,
        content: line!.content,
        type: 'add',
        highlight: line!.charChanges,
      });
      i++;
    } else {
      i++;
    }
  }
  return { left, right };
}

export function insertGapLines(lines: SideBySideLine[], count: number): SideBySideLine[] {
  const gaps: SideBySideLine[] = [];
  for (let i = 0; i < count; i++) {
    gaps.push({ lineNo: undefined, content: '', type: 'gap' });
  }
  return [...lines, ...gaps];
}

export function sideBySideWidth(maxLineLength: number): number {
  const gutterWidth = 6;
  const padding = 2;
  return (maxLineLength + gutterWidth + padding) * 2 + 4;
}

// ═══ §4 INLINE DISPLAY — prepareInlineDiff, inlineAnnotation, wordHighlight ═══

export const CHANGE_MARKER_START = '\x00';
export const CHANGE_MARKER_END = '\x01';

export interface InlineDiffLine {
  readonly lineNo: number | undefined;
  readonly content: string;
  readonly type: 'equal' | 'add' | 'delete';
  readonly annotatedContent?: string;
}

export function prepareInlineDiff(hunks: DiffHunk[]): InlineDiffLine[] {
  const result: InlineDiffLine[] = [];
  for (const hunk of hunks) {
    let i = 0;
    while (i < hunk.lines.length) {
      const line = hunk.lines[i];
      if (line!.type === 'equal') {
        result.push({
          lineNo: line!.oldLineNumber ?? line!.newLineNumber,
          content: line!.content,
          type: 'equal',
        });
        i++;
      } else if (line!.type === 'delete') {
        const delLine = line;
        const nextLine = i + 1 < hunk.lines.length ? hunk.lines[i + 1] : undefined;
        if (nextLine && nextLine.type === 'add') {
          const annotatedOld = inlineAnnotation(delLine!.content, delLine!.charChanges);
          const annotatedNew = inlineAnnotation(nextLine.content, nextLine.charChanges);
          result.push({
            lineNo: delLine!.oldLineNumber,
            content: delLine!.content,
            type: 'delete',
            annotatedContent: annotatedOld,
          });
          result.push({
            lineNo: nextLine.newLineNumber,
            content: nextLine.content,
            type: 'add',
            annotatedContent: annotatedNew,
          });
          i += 2;
        } else {
          result.push({
            lineNo: delLine!.oldLineNumber,
            content: delLine!.content,
            type: 'delete',
          });
          i++;
        }
      } else if (line!.type === 'add') {
        result.push({
          lineNo: line!.newLineNumber,
          content: line!.content,
          type: 'add',
        });
        i++;
      } else {
        i++;
      }
    }
  }
  return result;
}

export function inlineAnnotation(line: string, charChanges?: CharRange[]): string {
  if (!charChanges || charChanges.length === 0) return line;

  let result = '';
  let lastEnd = 0;
  for (const change of charChanges) {
    result += line.substring(lastEnd, change.startColumn);
    result += CHANGE_MARKER_START;
    result += line.substring(change.startColumn, change.endColumn);
    result += CHANGE_MARKER_END;
    lastEnd = change.endColumn;
  }
  result += line.substring(lastEnd);
  return result;
}

export function wordHighlight(oldLine: string, newLine: string): { oldAnnotated: string; newAnnotated: string } {
  const oldWords = oldLine.split(/(\s+)/);
  const newWords = newLine.split(/(\s+)/);

  const lcsResult = lcsWordArray(oldWords, newWords);
  const lcSet = new Set(lcsResult.map((_, i) => i));

  let oldAnnotated = '';
  let oldLcsIdx = 0;
  for (const word of oldWords) {
    if (oldLcsIdx < lcsResult.length && word === lcsResult[oldLcsIdx]) {
      oldAnnotated += word;
      oldLcsIdx++;
    } else {
      oldAnnotated += CHANGE_MARKER_START + word + CHANGE_MARKER_END;
    }
  }

  let newAnnotated = '';
  let newLcsIdx = 0;
  for (const word of newWords) {
    if (newLcsIdx < lcsResult.length && word === lcsResult[newLcsIdx]) {
      newAnnotated += word;
      newLcsIdx++;
    } else {
      newAnnotated += CHANGE_MARKER_START + word + CHANGE_MARKER_END;
    }
  }

  return { oldAnnotated, newAnnotated };
}

function lcsWordArray(a: string[], b: string[]): string[] {
  const m = a.length;
  const n = b.length;
  const dp: number[][] = Array.from({ length: m + 1 }, () => Array(n + 1).fill(0));
  for (let i = 1; i <= m; i++) {
    for (let j = 1; j <= n; j++) {
      if (a[i - 1] === b[j - 1]) dp[i]![j] = dp[i - 1]![j - 1]! + 1;
      else dp[i]![j] = Math.max(dp[i - 1]![j]!, dp[i]![j - 1]!);
    }
  }
  const result: string[] = [];
  let i = m;
  let j = n;
  while (i > 0 && j > 0) {
    if (a[i - 1] === b[j - 1]) {
      result.unshift(a[i - 1]!);
      i--;
      j--;
    } else if (dp[i - 1]![j]! > dp[i]![j - 1]!) {
      i--;
    } else {
      j--;
    }
  }
  return result;
}

// ═══ §5 DIFF NAVIGATION — nextChange, previousChange, changeAtLine, changeCount ═══

export function nextChange(hunks: DiffHunk[], currentLine: number): number | undefined {
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type !== 'equal') {
        const lineNum = line.oldLineNumber ?? line.newLineNumber ?? 0;
        if (lineNum > currentLine) return lineNum;
      }
    }
  }
  return undefined;
}

export function previousChange(hunks: DiffHunk[], currentLine: number): number | undefined {
  let lastChangeLine: number | undefined;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type !== 'equal') {
        const lineNum = line.oldLineNumber ?? line.newLineNumber ?? 0;
        if (lineNum < currentLine) lastChangeLine = lineNum;
        else return lastChangeLine;
      }
    }
  }
  return lastChangeLine;
}

export function changeAtLine(hunks: DiffHunk[], line: number): DiffLine | undefined {
  for (const hunk of hunks) {
    for (const dl of hunk.lines) {
      if (dl.type !== 'equal') {
        const lineNum = dl.oldLineNumber ?? dl.newLineNumber ?? 0;
        if (lineNum === line) return dl;
      }
    }
  }
  return undefined;
}

export function changeCount(hunks: DiffHunk[]): number {
  let count = 0;
  let inChange = false;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type !== 'equal') {
        if (!inChange) {
          count++;
          inChange = true;
        }
      } else {
        inChange = false;
      }
    }
    inChange = false;
  }
  return count;
}

export function firstChange(hunks: DiffHunk[]): number | undefined {
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type !== 'equal') {
        return line.oldLineNumber ?? line.newLineNumber;
      }
    }
  }
  return undefined;
}

export function lastChange(hunks: DiffHunk[]): number | undefined {
  let last: number | undefined;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type !== 'equal') {
        last = line.oldLineNumber ?? line.newLineNumber;
      }
    }
  }
  return last;
}

export function changeIndex(hunks: DiffHunk[], line: number): number {
  let idx = 0;
  let inChange = false;
  for (const hunk of hunks) {
    for (const dl of hunk.lines) {
      if (dl.type !== 'equal') {
        if (!inChange) {
          idx++;
          inChange = true;
        }
        const lineNum = dl.oldLineNumber ?? dl.newLineNumber ?? 0;
        if (lineNum === line) return idx;
      } else {
        inChange = false;
      }
    }
    inChange = false;
  }
  return -1;
}

// ═══ §6 DIFF DECORATIONS — diffToDecorations, additionDecoration, deletionDecoration ═══

export const DIFF_COLORS = {
  addition: '#2ea04370',
  deletion: '#f8514970',
  modification: '#d29922',
  moved: '#8b949e50',
} as const;

export function diffToDecorations(hunks: DiffHunk[], mode: DiffRenderMode): DecorationData[] {
  const decorations: DecorationData[] = [];
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      const lineNum = line.oldLineNumber ?? line.newLineNumber ?? 0;
      const range: Range = {
        start: { line: lineNum, column: 0 },
        end: { line: lineNum, column: line.content.length },
      };

      if (line.type === 'add') {
        decorations.push(additionDecoration(range));
      } else if (line.type === 'delete') {
        decorations.push(deletionDecoration(range));
      }
    }
  }
  return decorations;
}

export function additionDecoration(range: Range): DecorationData {
  return {
    id: asDecorationId(generateId('deco')),
    range,
    options: {
      isWholeLine: true,
      className: 'diff-addition',
      glyphMarginClassName: 'diff-addition-glyph',
      overviewRulerColor: DIFF_COLORS.addition,
      stickiness: 'never-grows',
    },
  };
}

export function deletionDecoration(range: Range): DecorationData {
  return {
    id: asDecorationId(generateId('deco')),
    range,
    options: {
      isWholeLine: true,
      className: 'diff-deletion',
      glyphMarginClassName: 'diff-deletion-glyph',
      overviewRulerColor: DIFF_COLORS.deletion,
      stickiness: 'never-grows',
    },
  };
}

export function modificationDecoration(range: Range): DecorationData {
  return {
    id: asDecorationId(generateId('deco')),
    range,
    options: {
      isWholeLine: false,
      className: 'diff-modification',
      inlineClassName: 'diff-modification-inline',
      overviewRulerColor: DIFF_COLORS.modification,
      stickiness: 'never-grows',
    },
  };
}

export function movedDecoration(range: Range): DecorationData {
  return {
    id: asDecorationId(generateId('deco')),
    range,
    options: {
      isWholeLine: true,
      className: 'diff-moved',
      glyphMarginClassName: 'diff-moved-glyph',
      overviewRulerColor: DIFF_COLORS.moved,
      stickiness: 'never-grows',
    },
  };
}

// ═══ §7 DIFF SUMMARY — diffSummaryText, diffSummaryShort, changeDescription, impactAssessment ═══

export function diffSummaryText(stats: DiffStats): string {
  const parts: string[] = [];
  if (stats.fileCount !== undefined) {
    parts.push(`${stats.fileCount} file${stats.fileCount !== 1 ? 's' : ''} changed`);
  }
  if (stats.additions > 0) {
    parts.push(`${stats.additions} insertion${stats.additions !== 1 ? 's' : ''}`);
  }
  if (stats.deletions > 0) {
    parts.push(`${stats.deletions} deletion${stats.deletions !== 1 ? 's' : ''}`);
  }
  if (parts.length === 0) return 'No changes';
  return parts.join(', ');
}

export function diffSummaryShort(stats: DiffStats): string {
  return `+${stats.additions} -${stats.deletions}`;
}

export function changeDescription(hunks: DiffHunk[]): string {
  const totalChanges = changeCount(hunks);
  let additions = 0;
  let deletions = 0;
  for (const hunk of hunks) {
    for (const line of hunk.lines) {
      if (line.type === 'add') additions++;
      else if (line.type === 'delete') deletions++;
    }
  }

  if (additions === 0 && deletions === 0) return 'No changes detected';
  if (additions > 0 && deletions === 0) return `${additions} line${additions !== 1 ? 's' : ''} added across ${totalChanges} change${totalChanges !== 1 ? 's' : ''}`;
  if (additions === 0 && deletions > 0) return `${deletions} line${deletions !== 1 ? 's' : ''} removed across ${totalChanges} change${totalChanges !== 1 ? 's' : ''}`;
  return `${additions} line${additions !== 1 ? 's' : ''} added, ${deletions} removed across ${totalChanges} change${totalChanges !== 1 ? 's' : ''}`;
}

export function impactAssessment(stats: DiffStats): 'trivial' | 'minor' | 'moderate' | 'major' | 'massive' {
  const total = stats.changes;
  if (total <= 5) return 'trivial';
  if (total <= 25) return 'minor';
  if (total <= 100) return 'moderate';
  if (total <= 500) return 'major';
  return 'massive';
}
